import { mutateInventories } from "@/api/inventories/useInventoriesQuery";
import { customFunctions } from "@/config/customFunction";
import { RawMaterialSelectLocationForm } from "@/features/inventory/components/Form/RawMaterialSelectLocationForm";
import { InputMaterialCheckViewForm } from "@/features/work/form/InputMaterialCheckViewForm";
import { useWorkActions } from "@/features/workByManagement/hooks/useWorkActions";
import { handleErrorResponse } from "@/utils/errorMessage";
import { Button } from "@mantine/core";
import {
  DefaultApiWmsFlushPutRequest,
  ProductionPlansGet200ResponseRowsInnerWorksInner,
  WorksItemGet200ResponseRowsInner,
  WorksSlittingScrapDetailPost200ResponseInner,
} from "@sizlcorp/sizl-api-document/dist/models";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { InvoiceStatementForm } from "../../invoiceStatement/form";
import { useModal } from "../../modal/ModalStackManager";
import { ExpectedDeliveryDateForm } from "../../operationOutsourceWorkByRow/components/expectedDeliveryDateForm";
import customAlert from "../../ui/alert/alert";
import useWorkMutation from "../../workByManagement/api/useWorkMutation";
import { useWorkState } from "../../workByManagement/hooks/useWorkState";
import { rowDataProps } from "../workSlittingTableInRowBody";

interface WorkSlittingLeftActionButtonBarProps {
  matchedData: Record<number, WorksSlittingScrapDetailPost200ResponseInner>;
}
export const WorkSlittingLeftActionButtonBar = (params: WorkSlittingLeftActionButtonBarProps) => {
  const { matchedData } = params;
  const { t } = useTranslation();
  const state = useWorkState();
  const actions = useWorkActions();
  const { openModal } = useModal();
  const { putEquipmentCorrectionMutate, workStartMutation, workEndMutation } = useWorkMutation();

  const queryClient = useQueryClient();
  const { mutate: flushMutate } = useMutation(
    (params: DefaultApiWmsFlushPutRequest) =>
      mutateInventories.flush(params).mutationFn(params as DefaultApiWmsFlushPutRequest | any),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["inventories"]);
        customAlert(t("workMessage.flushSuccess"), t("workButton.flush"), "info");
      },
      onError: (error) => {
        customAlert(t("workMessage.flushFail"), t("workButton.flush"), "red");
      },
    },
  );

  const selectedWorkIds = Object.values(state.scrapWorkIds || {}).map(Number);

  const filteredMatchedData = Object.values(matchedData).filter(
    (data) => selectedWorkIds.includes(data.workId ?? 0) && !!data.materialLotName,
  );

  return (
    <>
      <Button
        variant="light"
        disabled={
          !(
            state.scrapWorkIds &&
            Object.keys(state.scrapWorkIds || {}).length >= 1 &&
            state.works.some((data) => data.trackingStatus !== "DONE") &&
            !filteredMatchedData[0]?.materialLotName
          )
        }
        onClick={() => {
          const work = state.works?.find(
            (work) =>
              work.id !== undefined && Object.values(state.scrapWorkIds || {}).includes(work.id),
          );

          openModal(
            <RawMaterialSelectLocationForm
              workRowData={work as WorksItemGet200ResponseRowsInner}
            />,
            null,
            t("workButton.materialInput"),
            true,
          );
        }}
      >
        {t("workButton.materialInput")}
      </Button>
      <Button
        variant="light"
        color="violet"
        disabled={
          !(
            state.scrapWorkIds &&
            Object.keys(state.scrapWorkIds || {}).length >= 1 &&
            filteredMatchedData[0]?.materialLotName
          )
        }
        onClick={() => {
          const work = state.works?.find(
            (work) =>
              work.id !== undefined && Object.values(state.scrapWorkIds || {}).includes(work.id),
          );
          openModal(
            <InputMaterialCheckViewForm rowData={work as WorksItemGet200ResponseRowsInner} />,
            {},
            t("workButton.materialInputCheck"),
            true,
          );
        }}
      >
        {t("workButton.materialInputCheck")}
      </Button>
      <Button
        variant="light"
        color="orange"
        disabled={!(state.works && state.works.length === 1)}
        onClick={() => {
          const [work] = state.works;
          flushMutate({
            wmsFlushPutRequest: {
              locationCode:
                ((work as WorksItemGet200ResponseRowsInner)?.targetLocationCode ?? "") ||
                ((work as WorksItemGet200ResponseRowsInner)?.equipment?.targetLocationCode ?? "") ||
                ((work as WorksItemGet200ResponseRowsInner)?.routingData?.operation
                  ?.toLocationCode ??
                  ""),
            },
          });
        }}
      >
        {t("workButton.flush")}
      </Button>

      <Button
        variant="light"
        color="teal"
        disabled={!(state.works && state.works.length === 1)}
        onClick={async () => {
          const [work] = state.works as WorksItemGet200ResponseRowsInner[];
          if (work.id && work.equipmentCode && work.trackingStatus === "WORKING") {
            const result = await putEquipmentCorrectionMutate({
              equipmentsCorrectionPutRequest: {
                // equipmentCode: work.equipmentCode
                workId: work.id,
              },
            });
            result.data.length
              ? customAlert(t("workMessage.correctionSuccess"), t("workButton.correction"), "info")
              : customAlert(t("workMessage.correctionFail"), t("workButton.correction"), "red");
          } else {
            customAlert(t("workMessage.correctionFail2"), t("workButton.correction"), "red");
          }
        }}
      >
        {t("workButton.correction")}
      </Button>
      {customFunctions.ADD_OUTSOURCING_PRODUCTION_PLAN_BUTTON_AND_EXPECTED_DELIVERY_DATE && (
        <>
          <Button
            disabled={
              !(
                state.works &&
                state.works.length === 1 &&
                (state.works[0] as rowDataProps).slittingData
              )
            }
            onClick={() => {
              const works = state.works;
              workStartMutation(
                {
                  workId: works?.[0].id ?? 0,
                  worksWorkIdTrackingStartPostRequest: {
                    workId: works?.[0].id ?? 0,
                  },
                },
                {
                  onSuccess: () => {
                    customAlert(
                      t("workMessage.workStart"),
                      t("workStatus.timeTrackingStart"),
                      "info",
                    );
                    queryClient.invalidateQueries(["workPage"]);
                  },
                  onError: (error) => {
                    customAlert(
                      handleErrorResponse({
                        error,
                        errorContext: t("workStatus.timeTrackingStart"),
                      }),
                      t("workMessage.workStartFail"),
                      "red",
                    );
                  },
                },
              );
            }}
          >
            {t("workStatus.timeTrackingStart")}
          </Button>
          <Button
            color="red"
            disabled={
              !(
                state.works &&
                state.works.length === 1 &&
                (state.works[0] as rowDataProps).slittingData
              )
            }
            onClick={() => {
              const works = state.works;
              workEndMutation(
                {
                  workId: works?.[0].id ?? 0,
                  worksWorkIdTrackingStartPostRequest: {
                    workId: works?.[0].id ?? 0,
                  },
                },
                {
                  onSuccess: () => {
                    customAlert(t("workMessage.workEnd"), t("workStatus.timeTrackingEnd"), "info");
                    queryClient.invalidateQueries(["workPage"]);
                    actions.setWorks((prevWorks) => []);
                    actions.setScrapWorkIds([]);
                  },
                  onError: (error) => {
                    customAlert(
                      handleErrorResponse({
                        error,
                        errorContext: t("workStatus.timeTrackingEnd"),
                      }),
                      t("workMessage.workEndFail"),
                      "red",
                    );
                  },
                },
              );
            }}
          >
            {t("workStatus.timeTrackingEnd")}
          </Button>
          <Button
            color="green"
            disabled={!(state.scrapWorkIds && Object.keys(state.scrapWorkIds || {}).length >= 1)}
            onClick={() => {
              const slitting = Object.values(matchedData)?.find(
                (data) => data.workId === state.works[0].id,
              );

              openModal(
                <InvoiceStatementForm<"slitting">
                  pageType="slitting"
                  data={state.works[0] as WorksItemGet200ResponseRowsInner}
                  slittingData={slitting}
                />,
                null,
                t("invoice.invoice"),
              );
            }}
          >
            {t("invoice.invoice")}
          </Button>
          <Button
            color="grape"
            disabled={!(state.works && state.works.length === 1)}
            onClick={() => {
              openModal(
                <ExpectedDeliveryDateForm
                  workData={state.works as ProductionPlansGet200ResponseRowsInnerWorksInner[]}
                />,
                null,
                t("workButton.delivery"),
              );
            }}
          >
            {t("workButton.delivery")}
          </Button>
        </>
      )}
    </>
  );
};
